import type { AddressCandidate, NormalizedAddressRecord, SourceType } from "../../shared/types";

const STATES = new Set([
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC"
]);

const STATE_NAMES: Record<string, string> = {
  ALABAMA: "AL",
  ALASKA: "AK",
  ARIZONA: "AZ",
  ARKANSAS: "AR",
  CALIFORNIA: "CA",
  COLORADO: "CO",
  CONNECTICUT: "CT",
  DELAWARE: "DE",
  FLORIDA: "FL",
  GEORGIA: "GA",
  HAWAII: "HI",
  IDAHO: "ID",
  ILLINOIS: "IL",
  INDIANA: "IN",
  IOWA: "IA",
  KANSAS: "KS",
  KENTUCKY: "KY",
  LOUISIANA: "LA",
  MAINE: "ME",
  MARYLAND: "MD",
  MASSACHUSETTS: "MA",
  MICHIGAN: "MI",
  MINNESOTA: "MN",
  MISSISSIPPI: "MS",
  MISSOURI: "MO",
  MONTANA: "MT",
  NEBRASKA: "NE",
  NEVADA: "NV",
  "NEW HAMPSHIRE": "NH",
  "NEW JERSEY": "NJ",
  "NEW MEXICO": "NM",
  "NEW YORK": "NY",
  "NORTH CAROLINA": "NC",
  "NORTH DAKOTA": "ND",
  OHIO: "OH",
  OKLAHOMA: "OK",
  OREGON: "OR",
  PENNSYLVANIA: "PA",
  "RHODE ISLAND": "RI",
  "SOUTH CAROLINA": "SC",
  "SOUTH DAKOTA": "SD",
  TENNESSEE: "TN",
  TEXAS: "TX",
  UTAH: "UT",
  VERMONT: "VT",
  VIRGINIA: "VA",
  WASHINGTON: "WA",
  "WEST VIRGINIA": "WV",
  WISCONSIN: "WI",
  WYOMING: "WY",
  "DISTRICT OF COLUMBIA": "DC"
};

const stateAlternatives = [...STATES, ...Object.keys(STATE_NAMES)].sort((a, b) => b.length - a.length).join("|");
const stateZipPattern = new RegExp(`\\b(${stateAlternatives})\\s+(\\d{5}(?:-\\d{4})?)\\b`, "i");
const cityStatePattern = new RegExp(`^(.+?),?\\s+(${stateAlternatives})$`, "i");
const zipOnlyPattern = /^\d{5}(?:-\d{4})?$/;
const countryOnlyPattern = /^(?:US|USA|UNITED STATES|UNITED STATES OF AMERICA)$/i;
const poBoxPattern = /\bP\.?\s*O\.?\s*BOX\s*#?\s+\d+\b/i;
const streetSuffixPattern =
  /\b(ST|STREET|RD|ROAD|AVE|AVENUE|BLVD|BOULEVARD|DR|DRIVE|LN|LANE|CT|COURT|HWY|HIGHWAY|RTE|ROUTE|PKWY|PARKWAY|WAY|CIR|CIRCLE|PL|PLACE|TER|TERRACE|TRL|TRAIL)\b\.?/i;
const streetCityBoundaryPattern =
  /^(.+\b(?:ST|STREET|RD|ROAD|AVE|AVENUE|BLVD|BOULEVARD|DR|DRIVE|LN|LANE|CT|COURT|HWY|HIGHWAY|RTE|ROUTE|PKWY|PARKWAY|WAY|CIR|CIRCLE|PL|PLACE|TER|TERRACE|TRL|TRAIL)\b\.?)(?:\s+)([A-Z][A-Z .'-]*(?:\s+[A-Z][A-Z .'-]*)*)$/i;
const streetNumberPattern = /^\s*\d{1,6}\s+\S+/;
const unitPattern = /\b(APT|APARTMENT|UNIT|STE|SUITE|FLOOR|FL|BLDG|BUILDING|#)\b\.?/i;
const anchorPattern = /\b(ADDRESS|SHIP TO|SHIP FROM|DELIVER TO|CONSIGNEE|RECIPIENT|TO|FROM|BILL TO|INSURED|POLICYHOLDER|RESIDENCE|MAILING ADDRESS)\b/i;
const sectionPattern = /\b(INVOICE|TOTAL|SUBTOTAL|DATE|PHONE|EMAIL|FAX|TRACKING|ACCOUNT|POLICY|CLAIM|ORDER|QTY|DESCRIPTION)\b/i;
const brandingPattern = /\b(LOGO|TAGLINE|SLOGAN|POWERED BY|VISIT US|FOLLOW US|WWW\.|HTTPS?:\/\/|THANK YOU|SCAN|QR CODE)\b/i;
const organizationPattern = /\b(CO|COMPANY|CORP|CORPORATION|LLC|INC|LTD|LIMITED|SUPPLY|TRADING|WAREHOUSE|RETAIL|SHOP|STORE|GROUP|ASSOCIATES|SERVICES|INDUSTRIES)\b\.?/i;
const moneyPattern = /^\s*\$?\d+(?:\.\d{2})?\s*$/;
const emailPattern = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const phonePattern = /(?:\+?1[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}/;
const phoneOnlyPattern = /^\s*(?:\+?1[-.\s]?)?(?:\(?\d{3}\)?[-.\s]?)\d{3}[-.\s]?\d{4}\s*$/;
const longNumberPattern = /^\D*\d{12,}\D*$/;
const trackingPattern = /\b(1Z[0-9A-Z]{10,}|9\d{15,}|7\d{19,}|TBA\d{8,})\b/i;
const maxAddressGap = 8;

interface LineInfo {
  index: number;
  text: string;
  cityStateZip?: { city: string; state: string; postalCode: string };
  street: boolean;
  unit: boolean;
  ignoredReason?: string;
}

export function normalizeTextToCandidates(text: string, sourceType: SourceType, sourceFileName: string | null = null): AddressCandidate[] {
  const lines = normalizeLines(text);
  const ignoredExplanations = lines
    .filter((line) => line.ignoredReason)
    .map((line) => `ignored line ${line.index + 1}: ${line.ignoredReason}`);

  return lines
    .flatMap((line, candidateIndex) => {
      if (!line.cityStateZip) return [];
      const lookbehind = collectLookbehind(lines, line.index);
      const street = [...lookbehind].reverse().find((item) => item.street);
      if (!street) return [];
      const unit = [...lookbehind].reverse().find((item) => item.index > street.index && item.unit);
      const identityLines = lookbehind
        .filter((item) => item.index < street.index && isIdentityLine(item))
        .slice(-2);
      const identity = assignIdentity(identityLines);
      const splitStreet = splitInlineUnit(street.text);
      const rawLines = [...identityLines, street, ...(unit ? [unit] : []), line]
        .sort((a, b) => a.index - b.index)
        .map((item) => item.text);
      const record: NormalizedAddressRecord = {
        ...blankRecord(sourceType),
        sourceFileName,
        companyName: identity.company ?? null,
        contactName: identity.name ?? null,
        addressLine1: splitStreet.addressLine1,
        addressLine2: unit?.text ?? splitStreet.addressLine2,
        city: line.cityStateZip.city,
        stateProvince: line.cityStateZip.state,
        postalCode: line.cityStateZip.postalCode,
        phone: text.match(phonePattern)?.[0] ?? null,
        email: text.match(emailPattern)?.[0] ?? null,
        orderNumber: extractLabel(text, /order\s*#?:?\s*([A-Z0-9-]+)/i),
        referenceNumber: extractLabel(text, /ref(?:erence)?\s*#?:?\s*([A-Z0-9-]+)/i),
        trackingNumber: extractLabel(text, /tracking\s*#?:?\s*([A-Z0-9-]+)/i),
        poNumber: extractLabel(text, /p\.?o\.?\s*#?:?\s*([A-Z0-9-]+)/i),
        addressType: /bill/i.test(text) ? "bill_to" : /pickup/i.test(text) ? "pickup" : /return/i.test(text) ? "return" : "ship_to"
      };
      const scored = scoreRecord(record, rawLines, lookbehind);
      record.extractionConfidence = scored.score;
      return [
        {
          id: `candidate-${line.index}-${candidateIndex + 1}`,
          rawText: rawLines.join("\n"),
          rawLines,
          confidence: scored.score,
          record,
          warnings: [...validateRecord(record), ...scored.reasons, ...ignoredExplanations]
        }
      ];
    })
    .filter((candidate) => candidate.confidence >= 0.25)
    .sort((a, b) => b.confidence - a.confidence);
}

export function blankRecord(sourceType: SourceType): NormalizedAddressRecord {
  return {
    id: crypto.randomUUID(),
    companyName: null,
    contactName: null,
    addressLine1: "",
    addressLine2: null,
    addressLine3: null,
    city: "",
    stateProvince: "",
    postalCode: "",
    countryCode: "US",
    phone: null,
    email: null,
    orderNumber: null,
    referenceNumber: null,
    trackingNumber: null,
    poNumber: null,
    addressType: "ship_to",
    residential: null,
    sourceType,
    sourceFileName: null,
    extractionConfidence: null,
    validationStatus: "unconfirmed"
  };
}

export function validateRecord(record: NormalizedAddressRecord): string[] {
  const warnings: string[] = [];
  if (!record.addressLine1) warnings.push("Address line 1 is required.");
  if (!record.city) warnings.push("City is required.");
  if (!record.stateProvince) warnings.push("State or province is required.");
  if (!record.postalCode) warnings.push("Postal code is required.");
  if (record.countryCode === "US" && !/^\d{5}(-\d{4})?$/.test(record.postalCode)) warnings.push("US ZIP code looks unusual.");
  if (record.email && !emailPattern.test(record.email)) warnings.push("Email looks invalid.");
  return warnings;
}

export function normalizeUsState(value: string): string {
  const state = value.replace(/\./g, "").replace(/\s+/g, " ").trim().toUpperCase();
  if (STATES.has(state)) return state;
  return STATE_NAMES[state] ?? state;
}

export function normalizePostalCode(value: string): string {
  const cleaned = normalizeOcrText(value).replace(/\s+/g, "");
  const digits = cleaned.replace(/\D/g, "");
  if (digits.length === 9) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  if (digits.length === 5) return digits;
  return cleaned.toUpperCase();
}

export function isCompleteUsAddress(record: NormalizedAddressRecord): boolean {
  return Boolean(
    record.addressLine1 &&
      record.city &&
      STATES.has(normalizeUsState(record.stateProvince)) &&
      /^\d{5}(?:-\d{4})?$/.test(normalizePostalCode(record.postalCode)) &&
      (poBoxPattern.test(record.addressLine1) || streetNumberPattern.test(record.addressLine1))
  );
}

function normalizeLines(text: string): LineInfo[] {
  const rawLines = commaExpandedText(normalizeOcrText(text))
    .split(/\r?\n/)
    .flatMap((line) => line.split(/\s{3,}/))
    .map((line) =>
      line
        .trim()
        .replace(/^[-*•]\s*/, "")
        .replace(/^\|/, "")
        .replace(/\|$/, "")
        .replace(/\s*\|\s*/g, " ")
        .replace(/^(ship\s*to|deliver\s*to|recipient|address|name|phone|email):\s*/i, "")
        .replace(/\s+/g, " ")
        .replace(/,\s*$/, "")
        .trim()
    )
    .map((line) => normalizePoBox(line))
    .map(stripTrailingCountry)
    .filter((line) => line && !/^[-:| ]+$/.test(line) && !countryOnlyPattern.test(line));
  return mergeSplitCityStateZip(splitStreetCityLines(rawLines)).map((line, index) => {
    const cityStateZip = parseCityStateZip(line) ?? undefined;
    const street = isStreetLine(line);
    const unit = unitPattern.test(line);
    return {
      index,
      text: line,
      cityStateZip,
      street,
      unit,
      ignoredReason: classifyIgnoredLine(line, { cityStateZip: Boolean(cityStateZip), street, unit })
    };
  });
}

function commaExpandedText(text: string): string {
  if (text.includes("\n")) return text;
  const parts = text
    .split(/\s*,\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length >= 3 ? parts.join("\n") : text;
}

function normalizeOcrText(text: string): string {
  return text
    .normalize("NFKC")
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, "-")
    .replace(/(\d{5})-\d{1,3}\b/g, "$1")
    .replace(/(\d{5})-\b(?!\d{4})/g, "$1")
    .replace(/(\d{5})-\s*($|\n|,)/g, "$1$2");
}

function normalizePoBox(value: string): string {
  return value.replace(/\bP\.?\s*O\.?\s*BOX\s*#?\s+(\d+)\b/gi, "PO Box $1");
}

function stripTrailingCountry(value: string): string {
  return value
    .replace(/\s*,?\s+(US|USA|United States|United States of America)$/i, "")
    .trim();
}

function parseCityStateZip(value: string): { city: string; state: string; postalCode: string } | null {
  const compact = value.replace(/[,]+/g, " ").replace(/\s+/g, " ").trim();
  const match = compact.match(stateZipPattern);
  if (!match || match.index === undefined) return null;
  const state = normalizeUsState(match[1]);
  if (!STATES.has(state)) return null;
  const city = compact.slice(0, match.index).replace(/[,]+$/g, "").trim();
  if (!city || /\d/.test(city)) return null;
  return { city, state, postalCode: normalizePostalCode(match[2]) };
}

function isStreetLine(value: string): boolean {
  const text = value.replace(/\s+/g, " ").trim();
  if (phoneOnlyPattern.test(text) || longNumberPattern.test(text) || trackingPattern.test(text)) return false;
  return poBoxPattern.test(text) || (streetNumberPattern.test(text) && streetSuffixPattern.test(text));
}

function splitInlineUnit(value: string): { addressLine1: string; addressLine2: string | null } {
  const match = value.match(/\s+\b(APT|APARTMENT|UNIT|STE|SUITE|FLOOR|FL|BLDG|BUILDING|#)\b\.?\s+(.+)$/i);
  if (!match || match.index === undefined) return { addressLine1: value, addressLine2: null };
  return {
    addressLine1: value.slice(0, match.index).trim(),
    addressLine2: `${match[1]} ${match[2]}`.trim()
  };
}

function mergeSplitCityStateZip(lines: string[]): string[] {
  const merged: string[] = [];
  for (let index = 0; index < lines.length; index += 1) {
    const current = lines[index];
    const next = lines[index + 1];
    if (next && isStateZipOnly(next) && !parseCityStateZip(current)) {
      merged.push(`${current} ${next}`);
      index += 1;
    } else if (next && cityStatePattern.test(current) && zipOnlyPattern.test(next)) {
      merged.push(`${current} ${next}`);
      index += 1;
    } else {
      merged.push(current);
    }
  }
  return merged;
}

function splitStreetCityLines(lines: string[]): string[] {
  const split: string[] = [];
  for (let index = 0; index < lines.length; index += 1) {
    const current = lines[index];
    const next = lines[index + 1];
    const inline = splitInlineStreetCityStateZip(current);
    if (inline) {
      split.push(...inline);
      continue;
    }
    const commaParts = current.split(/\s*,\s*/).map((part) => part.trim()).filter(Boolean);
    if (next && isStateZipOnly(next) && commaParts.length >= 2) {
      const street = commaParts.slice(0, -1).join(", ");
      const city = commaParts[commaParts.length - 1];
      if (isStreetLine(street) && city && !/\d/.test(city)) {
        split.push(street, city);
        continue;
      }
    }
    const currentWithoutTrailingComma = current.replace(/,\s*$/, "");
    if (next && isStateZipOnly(next) && streetCityBoundaryPattern.test(currentWithoutTrailingComma)) {
      const match = currentWithoutTrailingComma.match(streetCityBoundaryPattern);
      if (match) {
        split.push(match[1].trim(), `${match[2].replace(/,$/, "").trim()} ${next}`);
        index += 1;
        continue;
      }
    }
    split.push(current);
  }
  return split;
}

function splitInlineStreetCityStateZip(value: string): string[] | null {
  const compact = value.replace(/\s+/g, " ").trim();
  const stateZip = compact.match(stateZipPattern);
  if (!stateZip || stateZip.index === undefined) return null;

  const beforeState = compact.slice(0, stateZip.index).replace(/,\s*$/, "").trim();
  if (!streetNumberPattern.test(beforeState) || !streetSuffixPattern.test(beforeState)) return null;

  const boundary = beforeState.match(streetCityBoundaryPattern);
  if (!boundary) return null;

  const street = boundary[1].trim();
  const city = boundary[2].replace(/,\s*$/, "").trim();
  const state = normalizeUsState(stateZip[1]);
  const postalCode = normalizePostalCode(stateZip[2]);
  if (!isStreetLine(street) || !city || /\d/.test(city) || !STATES.has(state)) return null;

  return [street, `${city} ${state} ${postalCode}`];
}

function isStateZipOnly(value: string): boolean {
  return new RegExp(`^(${stateAlternatives})\\s+\\d{5}(?:-\\d{4})?$`, "i").test(value);
}

function collectLookbehind(lines: LineInfo[], cityLineIndex: number): LineInfo[] {
  const context: LineInfo[] = [];
  let addressGap = 0;
  let foundStreet = false;
  let identityCount = 0;
  for (let offset = cityLineIndex - 1; offset >= 0 && context.length < 5; offset -= 1) {
    const line = lines[offset];
    if (!line) break;
    if (line.ignoredReason || anchorPattern.test(line.text)) continue;
    addressGap += 1;
    if (addressGap > maxAddressGap) break;
    context.unshift(line);
    if (line.street) {
      foundStreet = true;
      continue;
    }
    if (foundStreet && isIdentityLine(line)) {
      identityCount += 1;
      if (identityCount >= 2) break;
    }
  }
  return context;
}

function classifyIgnoredLine(
  text: string,
  roles: { cityStateZip: boolean; street: boolean; unit: boolean }
): string | undefined {
  if (roles.cityStateZip || roles.street || roles.unit || anchorPattern.test(text)) return undefined;
  if (phoneOnlyPattern.test(text)) return "phone number";
  if (trackingPattern.test(text)) return "tracking number";
  if (longNumberPattern.test(text)) return "barcode-like number";
  if (moneyPattern.test(text)) return "amount or numeric-only line";
  if (sectionPattern.test(text)) return "document header or non-address section";
  if (brandingPattern.test(text)) return "branding, slogan, or call-to-action";
  if (isProbableLogo(text)) return "probable logo or branding";
  return undefined;
}

function isIdentityLine(line: LineInfo): boolean {
  return !line.ignoredReason && !anchorPattern.test(line.text) && !line.street && !line.unit && !line.cityStateZip;
}

function assignIdentity(identityLines: LineInfo[]): { name?: string; company?: string } {
  if (identityLines.length === 0) return {};
  if (identityLines.length === 1) {
    const only = identityLines[0].text;
    return organizationPattern.test(only) ? { company: only } : { name: only };
  }
  const [first, second] = identityLines.slice(-2).map((line) => line.text);
  if (organizationPattern.test(first) && !organizationPattern.test(second)) return { company: first, name: second };
  if (!organizationPattern.test(first) && organizationPattern.test(second)) return { name: first, company: second };
  return { name: first, company: second };
}

function scoreRecord(
  record: NormalizedAddressRecord,
  rawLines: string[],
  context: LineInfo[] = []
): { score: number; reasons: string[] } {
  let score = 0;
  const reasons: string[] = [];
  const joined = rawLines.join(" ");
  if (record.stateProvince && STATES.has(record.stateProvince) && /^\d{5}(?:-\d{4})?$/.test(record.postalCode)) {
    score += 0.35;
    reasons.push("valid state and ZIP");
  }
  if (poBoxPattern.test(record.addressLine1)) {
    score += 0.35;
    reasons.push("PO Box detected");
  }
  if (streetNumberPattern.test(record.addressLine1)) {
    score += 0.2;
    reasons.push("street number detected");
  }
  if (streetSuffixPattern.test(record.addressLine1)) {
    score += 0.2;
    reasons.push("street suffix detected");
  }
  if (context.some((line) => anchorPattern.test(line.text))) {
    score += 0.08;
    reasons.push("address anchor nearby");
  }
  if (record.contactName || record.companyName) {
    score += 0.06;
    reasons.push("name or company line detected");
  }
  if (!record.city || !record.addressLine1) {
    score -= 0.25;
    reasons.push("missing city or street");
  }
  if (rawLines.filter((line) => /^\d+$/.test(line)).length > 1) {
    score -= 0.16;
    reasons.push("too many numeric-only lines");
  }
  if (trackingPattern.test(joined)) {
    score -= 0.22;
    reasons.push("tracking number pattern nearby");
  }
  if (rawLines.some((line) => phoneOnlyPattern.test(line))) {
    score -= 0.18;
    reasons.push("phone-number-only line nearby");
  }
  if (rawLines.some((line) => longNumberPattern.test(line))) {
    score -= 0.18;
    reasons.push("barcode-like numeric string nearby");
  }
  return { score: Math.max(0, Math.min(1, Number(score.toFixed(2)))), reasons };
}

function isProbableLogo(text: string): boolean {
  const compact = text.replace(/[^A-Za-z& ]/g, "").trim();
  if (!compact) return false;
  const words = compact.split(/\s+/);
  const hasLowercase = /[a-z]/.test(text);
  return !hasLowercase && words.length === 1 && compact.length <= 18;
}

function findLastIndex<T>(items: T[], predicate: (item: T) => boolean): number {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (predicate(items[index])) return index;
  }
  return -1;
}

function extractLabel(text: string, pattern: RegExp): string | null {
  return text.match(pattern)?.[1] ?? null;
}
